"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { User } from "lucide-react";

import { zodResolver } from "@hookform/resolvers/zod";
import ChangePasswordDialog from "@/components/dialogs/setting/ChangePassword";
import { useDispatch, useSelector } from "react-redux";

import { LOCAL_STORAGE_KEY, SHOW_MESSAGE } from "@/lib/constant/constant";
import AlertMsg from "@/components/dialogs/common/AlertMsg";
import { setUserInfo } from "@/store/slices";

const initAlertMsg = {
  open: false,
  label: "",
  msg: "",
  isError: false,
  isLoading: false,
  btnText: "",
  canClose: false,
};

export default function SettingsPage() {
  const [isEditing, setIsEditing] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const { userInfo } = useSelector((state) => state.global);

  const [alertMsg, setAlertMsg] = useState(initAlertMsg);

  const [isUpdateProfileLoading, setIsUpdateProfileLoading] = useState(false);

  const dispatch = useDispatch();

  // Called when alert box closed
  const handleAlertClose = () => {
    setAlertMsg(initAlertMsg);
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex gap-3 items-center">
          <h1 className="text-responsive-2xl font-bold text-neutral-100">
            Settings
          </h1>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-white/20 flex mt-1 gap-2">
        <button
          className={`pb-2 ${"border-b-2 border-[#00D3F2] text-[#00D3F2] font-bold"}`}
        >
          Account settings
        </button>
      </div>

      <div className="px-0 py-6 sm:px-6 max-w-3xl">
        {/* Account Settings */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-neutral-100 mb-6">
            Account Settings
          </h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-responsive-sm font-normal">
                Registered Email
              </Label>
              <Input
                id="email"
                value={userInfo?.admin?.email || ""}
                readOnly
                className="form-dark-input mt-2 bg-neutral-101 border-0 h-10"
              />
            </div>
            <div>
              <Label
                htmlFor="password"
                className="text-responsive-sm font-normal"
              >
                Password
              </Label>
              <div className="flex items-center space-x-4">
                <Input
                  id="password"
                  type="password"
                  value="••••••••••••"
                  readOnly
                  className="form-dark-input mt-2 bg-neutral-101 border-0 h-10 flex-1"
                />
                <Button
                  variant="link"
                  className="text-neutral-100 hover:text-neutral-108 p-0 h-auto hover:no-underline"
                  onClick={() => setIsResetPasswordOpen(true)}
                >
                  Change password
                </Button>
              </div>
            </div>
            <div className="space-y-4">
              {" "}
              <span className="text-sm text-brand-119">
                ( last changed 23/09/24){" "}
              </span>
            </div>
          </div>
        </div>
      </div>

      <ChangePasswordDialog
        isResetPasswordOpen={isResetPasswordOpen}
        setIsResetPasswordOpen={setIsResetPasswordOpen}
        isLoading={false}
        userDetails={userInfo?.admin}
      />

      {/* Alert popup message component */}
      <AlertMsg
        label={alertMsg.label}
        msg={alertMsg.msg}
        open={alertMsg.open}
        onClose={handleAlertClose}
        confirmAction={handleAlertClose}
        isLoading={false}
        isError={alertMsg.isError}
        canClose={alertMsg.canClose}
        confirmationBtnText={alertMsg.btnText}
      />
    </div>
  );
}
